/**
 * Presence.js — live "who's here / who's editing" for shared resources,
 * Google-Docs / Canva style. Built on Supabase Realtime Presence (same
 * websocket the app already uses for live data). NEVER writes to the DB —
 * presence is ephemeral, so it can never cause data loss.
 *
 *   const me = { id: myId, name: empName(employees, myId) || 'Admin' };
 *   const { peers, setEditing } = usePresence('mockup:' + mockId, me);
 *   <PresenceBar users={peers} meId={me.id} />
 *   ...onFocus={() => setEditing(true)} onBlur={() => setEditing(false)}
 */
/* global globalThis */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from '../utils/supabaseClient';

const COLORS = ['#A98953', '#8FA3B0', '#8FB08A', '#B08F9E', '#9B8FB0', '#C79A5B', '#8FB0AB', '#C08079'];
export function colorFor(id) {
  let h = 0; const s = String(id || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return COLORS[h % COLORS.length];
}
function initials(name) {
  const parts = String(name || '?').trim().split(/\s+/).filter(Boolean);
  const r = parts.map(w => w[0]).slice(0, 2).join('').toUpperCase();
  return r || '?';
}

// A stable, human guest label for anonymous share-link viewers (per tab).
export function makeGuest(prefix) {
  const animals = ['Fox', 'Owl', 'Lynx', 'Heron', 'Ibex', 'Wren', 'Otter', 'Falcon', 'Marten', 'Crane'];
  const id = (globalThis.crypto && globalThis.crypto.randomUUID)
    ? globalThis.crypto.randomUUID() : String(Math.random()).slice(2);
  let h = 0; for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return { id: 'guest_' + id.slice(0, 8), name: (prefix || 'Guest') + ' ' + animals[h % animals.length] };
}

// Join a presence channel for one shared resource. me = { id, name }.
export function usePresence(channelKey, me) {
  const [peers, setPeers] = useState([]);
  const chanRef = useRef(null);
  const tabId = useRef(null);
  if (!tabId.current) {
    tabId.current = (globalThis.crypto && globalThis.crypto.randomUUID)
      ? globalThis.crypto.randomUUID() : String(Math.random()).slice(2);
  }
  const meta = useRef({});
  meta.current = { userId: (me && me.id) || 'anon', name: (me && me.name) || 'Someone', tabId: tabId.current, editing: meta.current.editing || false };

  useEffect(() => {
    if (!channelKey || !me || !me.id) return undefined;
    setPeers([]);                                   // don't show the old resource's people
    meta.current = { ...meta.current, editing: false };
    const channel = supabase.channel('presence:' + channelKey, { config: { presence: { key: tabId.current } } });
    chanRef.current = channel;
    const sync = () => {
      const state = channel.presenceState();
      const flat = [];
      Object.keys(state).forEach(k => (state[k] || []).forEach(m => flat.push(m)));
      setPeers(flat);
    };
    channel
      .on('presence', { event: 'sync' }, sync)
      .on('presence', { event: 'join' }, sync)
      .on('presence', { event: 'leave' }, sync)
      .subscribe(async (status) => { if (status === 'SUBSCRIBED') { try { await channel.track(meta.current); } catch (e) { /* */ } } });
    return () => { try { Promise.resolve(channel.untrack()).catch(() => {}); } catch (e) { /* */ } supabase.removeChannel(channel); chanRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelKey, me && me.id]);

  const setEditing = useCallback((v) => {
    meta.current = { ...meta.current, editing: !!v };
    if (chanRef.current) { try { Promise.resolve(chanRef.current.track(meta.current)).catch(() => {}); } catch (e) { /* */ } }
  }, []);

  // one entry per user (a person may have several tabs); editing = any tab editing
  const byUser = {};
  peers.forEach(m => {
    const k = m.userId;
    if (!byUser[k]) byUser[k] = { userId: k, name: m.name, editing: false };
    if (m.editing) byUser[k].editing = true;
  });
  const all = Object.keys(byUser).map(k => byUser[k]);
  const others = all.filter(u => u.userId !== (me && me.id));
  return { peers: all, others, setEditing };
}

// Avatar chips + "N here" / "X is editing…". Drop in near the top of a resource.
export function PresenceBar({ users, meId, style, dark }) {
  if (!users || users.length === 0) return null;
  const editing = users.filter(u => u.editing && u.userId !== meId);
  const label = editing.length > 0
    ? (editing.length === 1 ? editing[0].name + ' is editing…' : editing.length + ' people editing…')
    : (users.length === 1 ? 'Only you here' : users.length + ' people here');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, ...(style || {}) }}>
      <div style={{ display: 'flex' }}>
        {users.slice(0, 6).map((u, i) => (
          <div key={u.userId} title={u.name + (u.editing ? ' (editing)' : '') + (u.userId === meId ? ' (you)' : '')}
            style={{
              width: 28, height: 28, borderRadius: '50%', marginLeft: i ? -8 : 0,
              background: colorFor(u.userId), color: '#0A0A0C', fontSize: 11, fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '2px solid ' + (dark ? '#0A0A0C' : '#fff'),
              boxShadow: u.editing ? '0 0 0 2px #8FB08A' : 'none',
            }}>{initials(u.name)}</div>
        ))}
        {users.length > 6 && <div style={{ width: 28, height: 28, borderRadius: '50%', marginLeft: -8, background: '#20201d', color: '#EFEBE4', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid ' + (dark ? '#0A0A0C' : '#fff') }}>+{users.length - 6}</div>}
      </div>
      <span style={{ fontSize: 12, color: editing.length ? '#8FB08A' : (dark ? 'rgba(239,235,228,0.55)' : '#6B6255'), fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</span>
    </div>
  );
}

export default PresenceBar;
